import { z } from "zod";
import { BooleanQueryParamSchema } from "./common.js";

export const SensorDataTypeSchema = z.enum(["number", "boolean", "enum", "text", "duration"]);

// Sensor é SEMPRE posição de cartão: cardId+position OBRIGATÓRIOS
// (não há mais sensor legado com credencial própria) + escala analógica opcional.
const SensorBaseShape = {
  code: z.string().min(1).max(40),
  name: z.string().min(1).max(120),
  dataType: z.string().min(1).max(40),
  cardId: z.string().uuid(),
  position: z.number().int().min(0),
  rawMin: z.number().optional(),
  rawMax: z.number().optional(),
  engMin: z.number().optional(),
  engMax: z.number().optional(),
  unit: z.string().max(20).optional(),
};

// Escala analógica: os 4 limites juntos ou nenhum. Exportados p/ o catálogo do assistente
// reusar a MESMA regra ao estender o objeto com deviceId (fonte única — guardrail #5).
type ScaleFields = { rawMin?: number; rawMax?: number; engMin?: number; engMax?: number };
const SCALE_FIELDS = ["rawMin", "rawMax", "engMin", "engMax"] as const;

export const scaleComplete = (s: ScaleFields): boolean => {
  const present = SCALE_FIELDS.filter((f) => s[f] !== undefined).length;
  return present === 0 || present === 4;
};

export const SCALE_MSG_TEXT = "Escala analógica exige rawMin, rawMax, engMin e engMax juntos.";

/**
 * superRefine no lugar do `refine` com `path` fixo em `rawMin`: cada issue ancora no
 * campo que **faltou**, nunca num âncora fixo. Com o âncora fixo, `sensor.create` com `rawMin`
 * preenchido e `engMax` ausente apontava um campo PRESENTE — e o worker, que decide "obrigatório
 * ausente" olhando o valor no path do issue, classificava `custom` e deixava o comando morrer mudo
 * (furo no contrato). Mesmo defeito já corrigido no período de `simulation.run`. No
 * formulário o ganho é o mesmo: o erro aparece sob o campo em branco, não sempre sob "Raw mín".
 */
export const scaleSuperRefine = (s: ScaleFields, ctx: z.RefinementCtx): void => {
  if (scaleComplete(s)) return;
  for (const field of SCALE_FIELDS) {
    if (s[field] === undefined) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [field], message: SCALE_MSG_TEXT });
    }
  }
};

// Objeto base (sem refine) — reusado pelo catálogo via .extend(); o request schema o refina.
export const CreateSensorObjectSchema = z.object(SensorBaseShape);
export const CreateSensorRequestSchema = CreateSensorObjectSchema.superRefine(scaleSuperRefine);
export type CreateSensorRequest = z.infer<typeof CreateSensorRequestSchema>;

// Update aceita SÓ code/name/dataType. cardId/position/escala NÃO são editáveis
// por PATCH (o service os ignora); aceitá-los no schema fazia o PATCH retornar 200 sem efeito.
export const UpdateSensorRequestSchema = z
  .object({
    code: SensorBaseShape.code,
    name: SensorBaseShape.name,
    dataType: SensorBaseShape.dataType,
  })
  .partial();
export type UpdateSensorRequest = z.infer<typeof UpdateSensorRequestSchema>;

// Eixos de estado do sensor: cadastro (`state`) × conectividade (`status`).
// Schemas Zod apenas — os TIPOS `SensorState`/`SensorStatus` já vêm de `entities/` (Prisma).
export const SensorStateSchema = z.enum(["AVAILABLE", "LINKED", "NO_READING", "INCONSISTENT"]);
export const SensorStatusSchema = z.enum(["ONLINE", "OFFLINE", "NO_READING", "INCONSISTENT"]);

export const ListSensorsQuerySchema = z.object({
  state: SensorStateSchema.optional(),
  status: SensorStatusSchema.optional(),
  available: BooleanQueryParamSchema.optional(),
  archived: BooleanQueryParamSchema.optional(),
});

export const ReadingCountQuerySchema = z.object({ since: z.string().datetime().optional() });

// Vínculo ativo do sensor (1:1 enquanto ativo): parâmetro → máquina → linha.
// Necessário porque o vínculo de um sensor persistido não era lido server-side (ver research.md).
export const SensorActiveLinkSchema = z.object({
  linkId: z.string().uuid(),
  parameterId: z.string().uuid(),
  parameterName: z.string(),
  machineId: z.string().uuid(),
  machineName: z.string(),
  lineId: z.string().uuid(),
  lineName: z.string(),
});
export type SensorActiveLink = z.infer<typeof SensorActiveLinkSchema>;

// Resposta canônica do sensor (antes só existia o tipo no frontend).
export const SensorSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  dataType: z.string(),
  state: SensorStateSchema,
  status: SensorStatusSchema,
  cardId: z.string().uuid().nullable(),
  position: z.number().int().nullable(),
  unit: z.string().nullable(),
  lastReading: z.string().datetime().nullable(),
  lastValue: z.string().nullable(),
  card: z.object({ code: z.string(), device: z.object({ code: z.string() }) }).nullable(),
  activeLink: SensorActiveLinkSchema.nullable(),
  createdAt: z.string().datetime(),
  // `updatedAt` é `@updatedAt` (não-nulo) no schema Prisma — o contrato acompanha.
  updatedAt: z.string().datetime(),
  deletedAt: z.string().datetime().nullable(),
});
export type SensorResponse = z.infer<typeof SensorSchema>;

export const ListSensorsResponseSchema = z.object({
  data: z.array(SensorSchema),
  error: z.null(),
});
